import { ToastQueue } from "@adobe/react-spectrum"
import {
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useI18n } from "../i18n/useI18n"

type FilterableValue = number | string | null | undefined
type NumericFilterOperator = "<" | "<=" | "=" | ">" | ">="

export type TableFilterField<RecordType> = {
  key: string
  label?: string
  aliases?: readonly string[]
  type?: "number" | "text"
  getValues: (record: RecordType) => readonly FilterableValue[]
  getSuggestions?: (
    records: readonly RecordType[],
  ) => readonly FilterableValue[]
}

type ParsedFilterValue = {
  exact: boolean
  negate: boolean
  normalized: string
  value: string
}

type ParsedFilterTerm =
  | {
      kind: "field"
      key: string
      values: ParsedFilterValue[]
    }
  | {
      kind: "invalid"
    }
  | {
      kind: "number"
      key: string
      operator: NumericFilterOperator
      value: number
    }
  | {
      kind: "text"
      value: ParsedFilterValue
    }

type ParsedTableFilter = {
  terms: ParsedFilterTerm[]
}

type FilterToken = {
  end: number
  start: number
  text: string
}

type FilterSegment = {
  end: number
  start: number
  text: string
}

export type TableFilterSuggestion = {
  detail?: string
  id: string
  kind: "key" | "value"
  label: string
  nextCursor: number
  nextQuery: string
}

export type UseTableFilterResult<RecordType> = {
  appliedQuery: string
  applyPendingQuery: () => void
  appendFilterValue: (
    key: string,
    value: string,
    options?: {
      negate?: boolean
    },
  ) => void
  cancelPendingQuery: () => void
  clearQuery: () => void
  draftQuery: string
  filteredRecords: readonly RecordType[]
  hasPendingClickQuery: boolean
  setCursorPosition: (cursor: number | null) => void
  suggestions: TableFilterSuggestion[]
  updateDraftQuery: (nextQuery: string, nextCursor?: number) => void
}

export function formatFilterValue(
  value: string,
  options?: {
    exact?: boolean
    negate?: boolean
  },
): string {
  const exact = options?.exact ?? false
  const negate = options?.negate ?? false
  const trimmed = value.trim()
  const body = exact ? `"${trimmed}"` : trimmed
  return negate ? `-${body}` : body
}

function normalizeValue(value: string): string {
  return value.trim().toLowerCase()
}

function stringifyValue(value: FilterableValue): string | null {
  if (value == null) return null
  const stringValue = String(value).trim()
  return stringValue.length > 0 ? stringValue : null
}

function tokenizeFilterQuery(query: string): FilterToken[] {
  const tokens: FilterToken[] = []
  let start = -1
  let inQuotes = false

  for (let index = 0; index < query.length; index += 1) {
    const char = query[index]

    if (char === '"') {
      if (start === -1) start = index
      inQuotes = !inQuotes
      continue
    }

    if (!inQuotes && /\s/u.test(char)) {
      if (start !== -1) {
        tokens.push({
          start,
          end: index,
          text: query.slice(start, index),
        })
        start = -1
      }
      continue
    }

    if (start === -1) {
      start = index
    }
  }

  if (start !== -1) {
    tokens.push({
      start,
      end: query.length,
      text: query.slice(start),
    })
  }

  return tokens
}

function findDelimiterOutsideQuotes(value: string, delimiter: string): number {
  let inQuotes = false

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && char === delimiter) {
      return index
    }
  }

  return -1
}

function splitOutsideQuotes(value: string, delimiter: string): string[] {
  const segments: string[] = []
  let start = 0
  let inQuotes = false

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && char === delimiter) {
      segments.push(value.slice(start, index))
      start = index + 1
    }
  }

  segments.push(value.slice(start))
  return segments
}

function parseValueSegment(rawSegment: string): ParsedFilterValue | null {
  return parseValueSegmentWithDefault(rawSegment)
}

function parseValueSegmentWithDefault(
  rawSegment: string,
  defaultExact = false,
): ParsedFilterValue | null {
  const trimmed = rawSegment.trim()
  if (!trimmed) return null

  const negate = trimmed.startsWith("-")
  const rawValue = negate ? trimmed.slice(1).trimStart() : trimmed
  if (!rawValue) return null

  const isQuoted = rawValue.startsWith('"') && rawValue.endsWith('"')
  const exact = isQuoted ? true : defaultExact
  const value = isQuoted ? rawValue.slice(1, -1) : rawValue
  const normalized = normalizeValue(value)
  if (!normalized) return null

  return {
    exact,
    negate,
    normalized,
    value,
  }
}

function getFieldAliasIndex<RecordType>(
  fields: readonly TableFilterField<RecordType>[],
): Map<string, TableFilterField<RecordType>> {
  const index = new Map<string, TableFilterField<RecordType>>()

  for (const field of fields) {
    index.set(normalizeValue(field.key), field)
    for (const alias of field.aliases ?? []) {
      index.set(normalizeValue(alias), field)
    }
  }

  return index
}

function resolveField<RecordType>(
  rawKey: string,
  fields: readonly TableFilterField<RecordType>[],
  options?: {
    allowPrefix?: boolean
  },
): TableFilterField<RecordType> | null {
  const normalizedKey = normalizeValue(rawKey)
  if (!normalizedKey) return null

  const aliasIndex = getFieldAliasIndex(fields)
  const exact = aliasIndex.get(normalizedKey)
  if (exact) return exact
  if (!options?.allowPrefix) return null

  const matches = fields.filter((field) => {
    const haystack = [field.key, ...(field.aliases ?? [])]
    return haystack.some((value) =>
      normalizeValue(value).startsWith(normalizedKey),
    )
  })

  return matches.length === 1 ? matches[0] : null
}

function isNumericField<RecordType>(
  field: TableFilterField<RecordType>,
): boolean {
  return field.type === "number"
}

function parseNumericValue(rawValue: string): number | null {
  if (
    rawValue.includes(",") ||
    rawValue.startsWith('"') ||
    rawValue.startsWith("-")
  ) {
    return null
  }

  const numericValue = Number(rawValue)
  return Number.isFinite(numericValue) ? numericValue : null
}

function findOperatorOutsideQuotes(
  value: string,
  operators: readonly string[],
): { operator: string; index: number } | null {
  let inQuotes = false

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (inQuotes) continue

    for (const operator of operators) {
      if (value.startsWith(operator, index)) {
        return {
          index,
          operator,
        }
      }
    }
  }

  return null
}

function parseComparisonToken<RecordType>(
  tokenText: string,
  fields: readonly TableFilterField<RecordType>[],
): ParsedFilterTerm | null {
  const operatorMatch = findOperatorOutsideQuotes(tokenText, [
    ">=",
    "<=",
    ">",
    "<",
    "=",
  ])
  if (!operatorMatch) return null

  const rawKey = tokenText.slice(0, operatorMatch.index)
  const rawValue = tokenText.slice(
    operatorMatch.index + operatorMatch.operator.length,
  )
  const field = resolveField(rawKey, fields, { allowPrefix: true })
  if (!field || !rawValue.trim()) {
    return null
  }

  if (operatorMatch.operator !== "=") {
    if (!isNumericField(field)) {
      return { kind: "invalid" }
    }

    const numericValue = parseNumericValue(rawValue.trim())
    if (numericValue == null) {
      return { kind: "invalid" }
    }

    return {
      kind: "number",
      key: field.key,
      operator: operatorMatch.operator as NumericFilterOperator,
      value: numericValue,
    }
  }

  if (isNumericField(field)) {
    const numericValue = parseNumericValue(rawValue.trim())
    if (numericValue != null) {
      return {
        kind: "number",
        key: field.key,
        operator: "=",
        value: numericValue,
      }
    }
  }

  const values = splitOutsideQuotes(rawValue, ",")
    .map((segment) => parseValueSegmentWithDefault(segment, true))
    .filter((segment): segment is ParsedFilterValue => segment != null)

  return values.length > 0
    ? {
        kind: "field",
        key: field.key,
        values,
      }
    : { kind: "invalid" }
}

function getFieldValues<RecordType>(
  field: TableFilterField<RecordType>,
  record: RecordType,
): string[] {
  const values: string[] = []

  for (const value of field.getValues(record)) {
    const stringValue = stringifyValue(value)
    if (stringValue) {
      values.push(stringValue)
    }
  }

  return values
}

function getAllFieldValues<RecordType>(
  record: RecordType,
  fields: readonly TableFilterField<RecordType>[],
): string[] {
  const values: string[] = []

  for (const field of fields) {
    values.push(...getFieldValues(field, record))
  }

  return values
}

function matchesParsedValue(
  candidateValues: readonly string[],
  parsedValue: ParsedFilterValue,
): boolean {
  return candidateValues.some((candidate) => {
    const normalizedCandidate = normalizeValue(candidate)
    return parsedValue.exact
      ? normalizedCandidate === parsedValue.normalized
      : normalizedCandidate.includes(parsedValue.normalized)
  })
}

export function parseTableFilter<RecordType>(
  query: string,
  fields: readonly TableFilterField<RecordType>[],
): ParsedTableFilter {
  const terms: ParsedFilterTerm[] = []

  for (const token of tokenizeFilterQuery(query)) {
    const separatorIndex = findDelimiterOutsideQuotes(token.text, ":")

    if (separatorIndex === -1) {
      const comparisonTerm = parseComparisonToken(token.text, fields)
      if (comparisonTerm) {
        terms.push(comparisonTerm)
        continue
      }

      const parsedValue = parseValueSegment(token.text)
      if (parsedValue) {
        terms.push({
          kind: "text",
          value: parsedValue,
        })
      }
      continue
    }

    const rawKey = token.text.slice(0, separatorIndex)
    const field = resolveField(rawKey, fields, { allowPrefix: true })
    const rawValue = token.text.slice(separatorIndex + 1)
    const values = splitOutsideQuotes(rawValue, ",")
      .map((segment) => parseValueSegment(segment))
      .filter((segment): segment is ParsedFilterValue => segment != null)

    if (field && values.length > 0) {
      terms.push({
        kind: "field",
        key: field.key,
        values,
      })
      continue
    }

    terms.push({ kind: "invalid" })
  }

  return { terms }
}

export function recordMatchesTableFilter<RecordType>(
  record: RecordType,
  fields: readonly TableFilterField<RecordType>[],
  parsedFilter: ParsedTableFilter,
): boolean {
  for (const term of parsedFilter.terms) {
    if (term.kind === "invalid") {
      return false
    }

    if (term.kind === "number") {
      const field = resolveField(term.key, fields, { allowPrefix: false })
      if (!field || !isNumericField(field)) {
        return false
      }

      const candidateNumbers = field
        .getValues(record)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))

      const matches = candidateNumbers.some((candidate) => {
        switch (term.operator) {
          case "<":
            return candidate < term.value
          case "<=":
            return candidate <= term.value
          case "=":
            return candidate === term.value
          case ">":
            return candidate > term.value
          case ">=":
            return candidate >= term.value
        }

        return false
      })

      if (!matches) {
        return false
      }
      continue
    }

    const field =
      term.kind === "field"
        ? resolveField(term.key, fields, { allowPrefix: false })
        : null
    if (term.kind === "field" && !field) {
      return false
    }

    const candidateValues =
      term.kind === "text" || field == null
        ? getAllFieldValues(record, fields)
        : getFieldValues(field, record)
    const values = term.kind === "text" ? [term.value] : term.values
    const positiveValues = values.filter((value) => !value.negate)
    const negativeValues = values.filter((value) => value.negate)

    if (
      negativeValues.some((value) => matchesParsedValue(candidateValues, value))
    ) {
      return false
    }

    if (
      positiveValues.length > 0 &&
      !positiveValues.some((value) =>
        matchesParsedValue(candidateValues, value),
      )
    ) {
      return false
    }
  }

  return true
}

function replaceQueryRange(
  query: string,
  start: number,
  end: number,
  replacement: string,
): string {
  return `${query.slice(0, start)}${replacement}${query.slice(end)}`
}

function getTokenAtCursor(query: string, cursor: number): FilterToken {
  const tokens = tokenizeFilterQuery(query)

  for (const token of tokens) {
    if (cursor >= token.start && cursor <= token.end) {
      return token
    }
  }

  return {
    start: cursor,
    end: cursor,
    text: "",
  }
}

function getSegmentAtCursor(value: string, cursor: number): FilterSegment {
  let start = 0
  let end = value.length
  let inQuotes = false

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]
    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }

    if (inQuotes || char !== ",") {
      continue
    }

    if (index < cursor) {
      start = index + 1
      continue
    }

    end = index
    break
  }

  return {
    start,
    end,
    text: value.slice(start, end),
  }
}

function sortSuggestions(values: readonly string[], partial: string): string[] {
  const normalizedPartial = normalizeValue(partial)

  return [...values].sort((left, right) => {
    const leftNormalized = normalizeValue(left)
    const rightNormalized = normalizeValue(right)
    const leftStarts = leftNormalized.startsWith(normalizedPartial)
    const rightStarts = rightNormalized.startsWith(normalizedPartial)

    if (leftStarts !== rightStarts) {
      return leftStarts ? -1 : 1
    }

    return left.localeCompare(right)
  })
}

function getFieldSuggestionValues<RecordType>(
  field: TableFilterField<RecordType>,
  records: readonly RecordType[],
): string[] {
  const rawValues =
    field.getSuggestions?.(records) ??
    records.flatMap((record) => getFieldValues(field, record))
  const deduped = new Map<string, string>()

  for (const value of rawValues) {
    const stringValue = stringifyValue(value)
    if (!stringValue) continue
    const normalized = normalizeValue(stringValue)
    if (!deduped.has(normalized)) {
      deduped.set(normalized, stringValue)
    }
  }

  if (!field.getSuggestions && deduped.size > 24) {
    return []
  }

  return [...deduped.values()].sort((left, right) => left.localeCompare(right))
}

export function getTableFilterSuggestions<RecordType>(options: {
  cursor: number
  defaultFieldDetail?: string
  fields: readonly TableFilterField<RecordType>[]
  query: string
  records: readonly RecordType[]
}): TableFilterSuggestion[] {
  const { cursor, defaultFieldDetail, fields, query, records } = options
  const token = getTokenAtCursor(query, cursor)
  const separatorIndex = findDelimiterOutsideQuotes(token.text, ":")

  if (separatorIndex === -1) {
    const partialKey = normalizeValue(token.text)
    const matchingFields = fields.filter((field) => {
      const haystack = [field.key, ...(field.aliases ?? [])]
      return partialKey.length === 0
        ? true
        : haystack.some((value) => normalizeValue(value).startsWith(partialKey))
    })

    return matchingFields.slice(0, 8).map((field) => {
      const replacement = `${field.key}:`
      return {
        detail: field.label ?? defaultFieldDetail,
        id: `key:${field.key}`,
        kind: "key" as const,
        label: replacement,
        nextCursor: token.start + replacement.length,
        nextQuery: replaceQueryRange(
          query,
          token.start,
          token.end,
          replacement,
        ),
      }
    })
  }

  const rawKey = token.text.slice(0, separatorIndex)
  const field = resolveField(rawKey, fields, { allowPrefix: true })
  if (!field) {
    return []
  }

  const valueText = token.text.slice(separatorIndex + 1)
  const valueCursor = Math.max(0, cursor - token.start - separatorIndex - 1)
  const segment = getSegmentAtCursor(valueText, valueCursor)
  const trimmedSegment = segment.text.trim()
  const negate = trimmedSegment.startsWith("-")
  const rawPartial = negate
    ? trimmedSegment.slice(1).trimStart()
    : trimmedSegment
  const normalizedPartial = normalizeValue(rawPartial.replace(/^"/u, ""))
  const suggestions = sortSuggestions(
    getFieldSuggestionValues(field, records).filter((value) => {
      if (!normalizedPartial) return true
      return normalizeValue(value).includes(normalizedPartial)
    }),
    normalizedPartial,
  )

  return suggestions.slice(0, 8).map((value) => {
    const replacementValue = formatFilterValue(value, {
      exact: /\s/u.test(value),
      negate,
    })
    const nextToken = `${field.key}:${valueText.slice(0, segment.start)}${replacementValue}${valueText.slice(segment.end)}`
    const nextQuery = replaceQueryRange(
      query,
      token.start,
      token.end,
      nextToken,
    )

    return {
      detail: field.label ?? field.key,
      id: `value:${field.key}:${value}`,
      kind: "value" as const,
      label: value,
      nextCursor:
        token.start +
        field.key.length +
        1 +
        segment.start +
        replacementValue.length,
      nextQuery,
    }
  })
}

export function appendFilterValueToQuery<RecordType>(
  query: string,
  fields: readonly TableFilterField<RecordType>[],
  key: string,
  value: string,
  options?: {
    negate?: boolean
  },
): string {
  const field = resolveField(key, fields, { allowPrefix: true })
  if (!field) return query

  const nextValue = formatFilterValue(value, {
    exact: true,
    negate: options?.negate ?? false,
  })
  const normalizedNextValue = normalizeValue(value)
  const tokens = tokenizeFilterQuery(query)

  for (let index = tokens.length - 1; index >= 0; index -= 1) {
    const token = tokens[index]
    const separatorIndex = findDelimiterOutsideQuotes(token.text, ":")
    if (separatorIndex === -1) continue

    const tokenField = resolveField(
      token.text.slice(0, separatorIndex),
      fields,
      { allowPrefix: true },
    )
    if (!tokenField || tokenField.key !== field.key) continue

    const existingValues = splitOutsideQuotes(
      token.text.slice(separatorIndex + 1),
      ",",
    )
      .map((segment) => parseValueSegment(segment))
      .filter((segment): segment is ParsedFilterValue => segment != null)

    const duplicate = existingValues.some(
      (segment) =>
        segment.negate === (options?.negate ?? false) &&
        segment.exact &&
        segment.normalized === normalizedNextValue,
    )
    if (duplicate) {
      return query
    }

    const nextToken =
      token.text.endsWith(":") || token.text.endsWith(",")
        ? `${token.text}${nextValue}`
        : `${token.text},${nextValue}`

    return replaceQueryRange(query, token.start, token.end, nextToken)
  }

  const nextToken = `${field.key}:${nextValue}`
  return query.trim().length === 0 ? nextToken : `${query} ${nextToken}`
}

function formatNumericValue(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value)
}

function serializeV1Value(value: ParsedFilterValue): string | null {
  if (value.value.includes('"')) {
    return null
  }

  if (value.exact) {
    const needsQuotes = /[\s,;:#<>=]/u.test(value.value)
    const body = needsQuotes ? `"${value.value}"` : value.value
    return value.negate ? `-${body}` : body
  }

  if (/[\s,;:#<>=]/u.test(value.value)) {
    return null
  }

  return value.negate ? `-${value.value}` : value.value
}

function serializeVersion1<RecordType>(
  parsedFilter: ParsedTableFilter,
  _fields: readonly TableFilterField<RecordType>[],
): string | null {
  const clauses: string[] = []

  for (const term of parsedFilter.terms) {
    if (term.kind === "invalid") {
      return null
    }

    if (term.kind === "number") {
      clauses.push(
        `${term.key}${term.operator}${formatNumericValue(term.value)}`,
      )
      continue
    }

    if (term.kind === "text") {
      const serializedValue = serializeV1Value(term.value)
      if (!serializedValue) return null
      clauses.push(serializedValue)
      continue
    }

    const exactModes = new Set(term.values.map((value) => value.exact))
    if (exactModes.size > 1) {
      return null
    }

    const serializedValues = term.values.map((value) => serializeV1Value(value))
    if (serializedValues.some((value) => value == null)) {
      return null
    }

    clauses.push(
      `${term.key}${term.values[0]?.exact ? "=" : ":"}${serializedValues.join(",")}`,
    )
  }

  return clauses.join(";")
}

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = ""
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }

  return btoa(binary)
    .replace(/\+/gu, "-")
    .replace(/\//gu, "_")
    .replace(/=+$/gu, "")
}

function decodeBase64Url(encoded: string): Uint8Array {
  const padded = encoded
    .replace(/-/gu, "+")
    .replace(/_/gu, "/")
    .padEnd(Math.ceil(encoded.length / 4) * 4, "=")

  const binary = atob(padded)
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

function compressFilterQuery(query: string): Uint16Array {
  const bytes = new TextEncoder().encode(query)
  if (bytes.length === 0) {
    return new Uint16Array()
  }

  const dictionary = new Map<string, number>()
  for (let index = 0; index < 256; index += 1) {
    dictionary.set(String(index), index)
  }

  let phrase = String(bytes[0])
  let nextCode = 256
  const output: number[] = []

  for (let index = 1; index < bytes.length; index += 1) {
    const current = String(bytes[index])
    const candidate = `${phrase},${current}`
    if (dictionary.has(candidate)) {
      phrase = candidate
      continue
    }

    const phraseCode = dictionary.get(phrase)
    if (phraseCode == null) {
      throw new Error("Could not encode the table filter.")
    }
    output.push(phraseCode)
    dictionary.set(candidate, nextCode)
    nextCode += 1
    phrase = current
  }

  const finalCode = dictionary.get(phrase)
  if (finalCode == null) {
    throw new Error("Could not encode the table filter.")
  }
  output.push(finalCode)
  return Uint16Array.from(output)
}

function decompressFilterQuery(codes: Uint16Array): string {
  if (codes.length === 0) {
    return ""
  }

  const dictionary = new Map<number, number[]>([])
  for (let index = 0; index < 256; index += 1) {
    dictionary.set(index, [index])
  }

  let phrase = dictionary.get(codes[0])
  if (!phrase) {
    throw new Error("Invalid compressed filter payload.")
  }

  const output = [...phrase]
  let nextCode = 256

  for (let index = 1; index < codes.length; index += 1) {
    const code = codes[index]
    const entry: number[] = dictionary.get(code) ?? [...phrase, phrase[0]]
    output.push(...entry)
    dictionary.set(nextCode, [...phrase, entry[0]])
    nextCode += 1
    phrase = entry
  }

  return new TextDecoder().decode(Uint8Array.from(output))
}

function encodeVersion2(query: string): string {
  const codes = compressFilterQuery(query)
  const bytes = new Uint8Array(codes.length * 2)

  for (let index = 0; index < codes.length; index += 1) {
    const code = codes[index]
    bytes[index * 2] = code >> 8
    bytes[index * 2 + 1] = code & 0xff
  }

  return encodeBase64Url(bytes)
}

function decodeVersion2(encoded: string): string {
  const bytes = decodeBase64Url(encoded)
  if (bytes.length % 2 !== 0) {
    throw new Error("Invalid compressed filter payload.")
  }

  const codes = new Uint16Array(bytes.length / 2)
  for (let index = 0; index < codes.length; index += 1) {
    codes[index] = (bytes[index * 2] << 8) | bytes[index * 2 + 1]
  }

  return decompressFilterQuery(codes)
}

function normalizeParsedFilter(parsedFilter: ParsedTableFilter): string {
  return JSON.stringify(parsedFilter.terms)
}

export function serializeTableFilterToFragment<RecordType>(
  query: string,
  fields: readonly TableFilterField<RecordType>[],
): string {
  if (query.trim().length === 0) {
    return ""
  }

  const parsedFilter = parseTableFilter(query, fields)
  const version1Body = serializeVersion1(parsedFilter, fields)
  const version2Fragment = `#2:${encodeVersion2(query)}`

  if (!version1Body) {
    return version2Fragment
  }

  const version1Query = splitOutsideQuotes(version1Body, ";").join(" ")
  if (
    normalizeParsedFilter(parseTableFilter(version1Query, fields)) !==
    normalizeParsedFilter(parsedFilter)
  ) {
    return version2Fragment
  }

  const version1Fragment = `#1:${version1Body}`
  return version2Fragment.length < version1Fragment.length
    ? version2Fragment
    : version1Fragment
}

export function decodeTableFilterFragment<RecordType>(
  fragment: string,
  fields: readonly TableFilterField<RecordType>[],
):
  | {
      ok: true
      query: string
    }
  | {
      error: string
      ok: false
    } {
  if (!fragment || fragment === "#") {
    return {
      ok: true,
      query: "",
    }
  }

  if (fragment.startsWith("#1:")) {
    const body = fragment.slice(3)
    if (!body) {
      return {
        ok: true,
        query: "",
      }
    }

    const clauses = splitOutsideQuotes(body, ";")
    if (clauses.some((clause) => clause.trim().length === 0)) {
      return {
        error: "Could not restore the table filter from the URL.",
        ok: false,
      }
    }

    const query = clauses.join(" ")
    const parsedFilter = parseTableFilter(query, fields)
    if (parsedFilter.terms.some((term) => term.kind === "invalid")) {
      return {
        error: "Could not restore the table filter from the URL.",
        ok: false,
      }
    }

    return {
      ok: true,
      query,
    }
  }

  if (fragment.startsWith("#2:")) {
    try {
      return {
        ok: true,
        query: decodeVersion2(fragment.slice(3)),
      }
    } catch {
      return {
        error: "Could not restore the table filter from the URL.",
        ok: false,
      }
    }
  }

  return {
    error: "Could not restore the table filter from the URL.",
    ok: false,
  }
}

export function useTableFilter<RecordType>(options: {
  fields: readonly TableFilterField<RecordType>[]
  records: readonly RecordType[]
}): UseTableFilterResult<RecordType> {
  const { t } = useI18n()
  const { fields, records } = options
  const [draftQuery, setDraftQuery] = useState("")
  const [appliedQuery, setAppliedQuery] = useState("")
  const [cursorPosition, setCursorPosition] = useState(0)
  const [hasPendingClickQuery, setHasPendingClickQuery] = useState(false)
  const [hasInitializedFragment, setHasInitializedFragment] = useState(false)

  const deferredQuery = useDeferredValue(appliedQuery)
  const parsedFilter = useMemo(
    () => parseTableFilter(deferredQuery, fields),
    [deferredQuery, fields],
  )
  const filteredRecords = useMemo(
    () =>
      parsedFilter.terms.length === 0
        ? [...records]
        : records.filter((record) =>
            recordMatchesTableFilter(record, fields, parsedFilter),
          ),
    [fields, parsedFilter, records],
  )
  const suggestions = useMemo(
    () =>
      getTableFilterSuggestions({
        cursor: cursorPosition,
        defaultFieldDetail: t("filter.fieldDetail"),
        fields,
        query: draftQuery,
        records,
      }),
    [cursorPosition, draftQuery, fields, records, t],
  )

  const applyQuery = (nextQuery: string, nextCursor = nextQuery.length) => {
    setDraftQuery(nextQuery)
    setCursorPosition(nextCursor)
    setHasPendingClickQuery(false)
    startTransition(() => {
      setAppliedQuery(nextQuery)
    })
  }

  const stageQuery = (nextQuery: string, nextCursor = nextQuery.length) => {
    setDraftQuery(nextQuery)
    setCursorPosition(nextCursor)
    setHasPendingClickQuery(true)
  }

  useEffect(() => {
    if (typeof window === "undefined") return

    const applyDecodedQuery = (nextQuery: string) => {
      setDraftQuery(nextQuery)
      setCursorPosition(nextQuery.length)
      setHasPendingClickQuery(false)
      startTransition(() => {
        setAppliedQuery(nextQuery)
      })
    }

    const syncFromFragment = () => {
      const decoded = decodeTableFilterFragment(window.location.hash, fields)

      if (decoded.ok) {
        applyDecodedQuery(decoded.query)
      } else {
        applyDecodedQuery("")
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${window.location.search}`,
        )
        ToastQueue.negative(t("filter.restoreError"))
      }

      setHasInitializedFragment(true)
    }

    syncFromFragment()
    window.addEventListener("hashchange", syncFromFragment)
    return () => {
      window.removeEventListener("hashchange", syncFromFragment)
    }
  }, [fields, t])

  useEffect(() => {
    if (!hasInitializedFragment || typeof window === "undefined") {
      return
    }

    const nextFragment = serializeTableFilterToFragment(appliedQuery, fields)
    const nextUrl = `${window.location.pathname}${window.location.search}${nextFragment}`
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`

    if (nextUrl !== currentUrl) {
      window.history.replaceState({}, "", nextUrl)
    }
  }, [appliedQuery, fields, hasInitializedFragment])

  return {
    appliedQuery,
    applyPendingQuery() {
      applyQuery(draftQuery, draftQuery.length)
    },
    appendFilterValue(
      key: string,
      value: string,
      options?: { negate?: boolean },
    ) {
      const nextQuery = appendFilterValueToQuery(
        draftQuery,
        fields,
        key,
        value,
        {
          negate: options?.negate,
        },
      )
      stageQuery(nextQuery, nextQuery.length)
    },
    cancelPendingQuery() {
      setDraftQuery(appliedQuery)
      setCursorPosition(appliedQuery.length)
      setHasPendingClickQuery(false)
    },
    clearQuery() {
      applyQuery("", 0)
    },
    draftQuery,
    filteredRecords,
    hasPendingClickQuery,
    setCursorPosition(cursor: number | null) {
      if (cursor == null) return
      setCursorPosition(cursor)
    },
    suggestions,
    updateDraftQuery(nextQuery: string, nextCursor = nextQuery.length) {
      applyQuery(nextQuery, nextCursor)
    },
  }
}
